import { useMemo } from '@adornis/functional-lit/stateful.js';
import { useQuery, type QueryState } from '@adornis/functional-lit/use-query.js';
import type { Observable } from 'rxjs';

/**
 * `useQuery`, except that what is already on screen stays there while the next result is fetched.
 *
 * `useQuery` drops its value the instant its dependencies change, so a view that re-reads on a
 * live change signal renders its empty branch for the length of a roundtrip. That is not a
 * spinner in a corner: the empty branch is a different template, so lit tears the old subtree
 * down and builds a new one. Everything that subtree held goes with it — the tab that was open,
 * an editor in mid-correction, the scroll position — and all of it for a refresh that ends up
 * showing very nearly the same data. Since these views re-read whenever a colleague answers or a
 * reply mail is read, the screen did that unprompted, while someone was working in it.
 *
 * `identity` names what the kept value belongs to, normally the id of the record on screen. When
 * it changes the memory is dropped, so opening another request never shows the previous one's
 * data for a moment.
 *
 * `refreshing` is the honest replacement for the loading flag: a reload with content still
 * standing is not the same state as an empty view waiting for its first answer, and only the
 * latter may take the screen.
 */
export function useStickyQuery<T>(
  source: () => Observable<T> | Promise<T>,
  deps: unknown[],
  identity: unknown[],
): QueryState<T> & { refreshing: boolean } {
  const kept = useMemo<{ value: T | null | undefined }>(() => [{ value: undefined }], identity);
  const state = useQuery(source, deps);
  if (state.value !== undefined && state.value !== null) kept.value = state.value;

  const value = state.value ?? kept.value;
  return { ...state, value, refreshing: state.loading && value !== undefined && value !== null };
}
